import { useAuth0, withAuthenticationRequired } from '@auth0/auth0-react'
//import { Typography } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { unparse } from 'papaparse'
import { Box } from '../components/Box'
import Chart from '../components/Charts'
import {
  getStaticProps as getLayoutStaticProps,
  LayoutProps,
} from '../components/Layout'
import { Navbar } from '../components/NavBar'
import sanity from '../lib/sanity'
import styled from '@emotion/styled'
import {
  useGetDefaults,
  useGetGroupings,
  useGetIntervals,
  useLiveOccupancy,
  useQuery,
} from '../lib/hooks/api'
import { getLastWeeksDate, getTodayIcl } from '../lib/helpers'
import { Text14 } from '../components/Text'
import { DropDown } from '../components/Dropdown'
import { useCurrentPng } from 'recharts-to-png'
import { saveAs } from 'file-saver'

const Content = styled.div`
  min-height: 130vh;
  max-width: 1450px;
`
const BoxStyle = styled.div`
  display: flex;
  justify-content: space-around;
  padding-bottom: 50px;
  flex-wrap: wrap;
`
const BottomOptionsStyle = styled.div`
  display: flex;
  justify-content: start;
  align-items: center; 
  column-gap: 30px;
  padding:10px 40px;
}
`
const Index = () => {
  const { /*user,*/ isLoading } = useAuth0()
  const { data: { live_occupancy } = { live_occupancy: 0 } } =
    useLiveOccupancy()
  const [start, setStart] = useState<number>(getLastWeeksDate())
  const [end, setEnd] = useState<number>(getTodayIcl())
  const [entrance, setEntrance] = useState<string>('')
  const [isChecked, setIsChecked] = useState(false)

  const { data: intervals = {} } = useGetIntervals()
  const { data: groupings = {} } = useGetGroupings()
  const { data: defaults = { defaultInterval: '', defaultGrouping: '' } } =
    useGetDefaults()
  const [interval, setInterval] = useState<string>('')
  const [grouping, setGrouping] = useState<string>('')

  const {
    data: test = {
      grouped: [],
      averageFootfall: 0,
      percentChange: 0,
      barKeys: ['value'],
    },
  } = useQuery(start, end, interval, grouping, entrance)

  useEffect(() => {
    setInterval(defaults.defaultInterval)
    setGrouping(defaults.defaultGrouping)
  }, [defaults])

  const [getPng, utility] = useCurrentPng()

  const handleDownloadPNG = useCallback(async () => {
    const png = await getPng()

    // Verify that png is not undefined
    if (png) {
      // Download with FileSaver
      saveAs(
        png,
        `Footfall_${new Date(start).toLocaleDateString('en-GB')}-${new Date(
          end,
        ).toLocaleDateString('en-GB')}.png`,
      )
    }
  }, [getPng, end, start])

  const handleDownloadCSV = useCallback(async () => {
    const csv = new Blob([unparse(test.grouped)], {
      type: 'application/csv; charset=UTF-8',
    })

    // Download with FileSaver
    saveAs(
      csv,
      `Footfall_${new Date(start).toLocaleDateString('en-GB')}-${new Date(
        end,
      ).toLocaleDateString('en-GB')}.csv`,
    )
  }, [end, start, test.grouped])

  if (isLoading) return <div>Loading...</div>
  return (
    <Content>
      {/* <Typography variant="h6">Hello {user?.name}</Typography> */}
      <BoxStyle>
        <Box
          bgcolor="#EB3123"
          title="Average Daily Footfall"
          content={`${
            test.averageFootfall === null
              ? 'N/A'
              : test.averageFootfall.toFixed(0)
          }`}
          colorh2="#fff"
          colorp="#fff"
        />
        <Box
          bgcolor="#fff"
          colorh2="#060606"
          colorp="#EB3123"
          title="Change in Footfall Since Last Period"
          content={`${
            test.percentChange === null
              ? 'N/A'
              : test.percentChange.toFixed(0) + '%'
          }`}
        />
        <Box
          bgcolor="#EB3123"
          colorh2="#fff"
          colorp="#fff"
          title="Live Occupancy"
          content={live_occupancy.toString()}
        />
      </BoxStyle>
      <Navbar
        start={new Date(start)}
        setStart={setStart}
        setEnd={setEnd}
        setEntrance={setEntrance}
        handleDownloadPNG={handleDownloadPNG}
        handleDownloadCSV={handleDownloadCSV}
        isChecked={isChecked}
        setIsChecked={setIsChecked}
      />
      <Chart showWeather={isChecked} data={test} refForDownload={utility.ref} />
      <BottomOptionsStyle>
        <Text14>Readings Interval</Text14>
        <DropDown
          menuItems={Object.keys(intervals).map(
            (key) => intervals[key].displayName,
          )}
          defaultVal={1}
          mapping={Object.fromEntries(
            Object.keys(intervals).map((key) => [
              intervals[key].displayName,
              key,
            ]),
          )}
          callBack={setInterval}
        />
        <Text14>Group Readings By</Text14>
        <DropDown
          menuItems={Object.keys(groupings).map(
            (key) => groupings[key].displayName,
          )}
          defaultVal={0}
          mapping={Object.fromEntries(
            Object.keys(groupings).map((key) => [
              groupings[key].displayName,
              key,
            ]),
          )}
          callBack={setGrouping}
        />
      </BottomOptionsStyle>
    </Content>
  )
}

interface Props extends LayoutProps {
  title: string
}

export const getStaticProps = async (): Promise<{ props: Props }> => ({
  props: {
    ...(await sanity.fetch(
      `*[_type == 'settings' && _id == 'settings'][0] {
        "title": title
      }`,
    )),
    ...(await getLayoutStaticProps()).props,
  },
})

export default withAuthenticationRequired(Index)
